/**
 * Settings Manager – Persistiert und verwaltet Anwendungseinstellungen.
 * Speichert in localStorage.
 */

const STORAGE_KEY = 'the-reader-settings';
const UPDATED_KEY = 'the-reader-settings-updated';

/** Default settings */
const DEFAULTS = {
  apiKey: '',
  languageCode: 'de-DE',
  voiceName: '',
  ssmlGender: 'NEUTRAL',
  speakingRate: 1.0,
  pitch: 0,
  audioEncoding: 'MP3',
  textSize: 'normal',
  volume: 1.0,
  autoplay: true,
  retentionDays: 30,
};

/**
 * Free tier quotas per model tier (characters per month)
 */
export const FREE_TIER_QUOTAS = {
  Standard: 4_000_000,
  WaveNet: 1_000_000,
  Neural2: 1_000_000,
  Studio: 1_000_000,
  'Chirp 3 HD': 1_000_000,
  Chirp: 1_000_000,
  Polyglot: 1_000_000,
  News: 1_000_000,
};

/**
 * Prices per 1 million characters AFTER free tier
 */
export const PRICING = {
  Standard: 4.0,
  WaveNet: 16.0,
  Neural2: 16.0,
  Studio: 160.0,
  'Chirp 3 HD': 30.0,
  Chirp: 30.0,
  Polyglot: 16.0,
  News: 16.0,
};

/** @type {Object} Current settings in memory */
let currentSettings = { ...DEFAULTS };

/**
 * Wann jede Einstellung zuletzt geändert wurde (Timestamp je Schlüssel). Beim
 * Abgleich zwischen Geräten gewinnt pro Einstellung die jüngere Änderung.
 * @type {Object<string, number>}
 */
let updatedAt = {};

const changeListeners = [];

/**
 * Simple client-side XOR-based string obfuscation / encryption
 * @param {string} text
 * @returns {string}
 */
function encrypt(text) {
  if (!text) return '';
  const key = 'antigravity-secret-key-salt-2026';
  let result = '';
  for (let i = 0; i < text.length; i++) {
    const charCode = text.charCodeAt(i) ^ key.charCodeAt(i % key.length);
    result += String.fromCharCode(charCode);
  }
  // Safe base64 encoding
  return btoa(
    encodeURIComponent(result).replace(/%([0-9A-F]{2})/g, (match, p1) => {
      return String.fromCharCode(parseInt(p1, 16));
    })
  );
}

/**
 * Decrypts a previously obfuscated string
 * @param {string} encodedText
 * @returns {string}
 */
function decrypt(encodedText) {
  if (!encodedText) return '';
  try {
    // Safe base64 decoding
    const text = decodeURIComponent(
      Array.prototype.map
        .call(atob(encodedText), (c) => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );

    const key = 'antigravity-secret-key-salt-2026';
    let result = '';
    for (let i = 0; i < text.length; i++) {
      const charCode = text.charCodeAt(i) ^ key.charCodeAt(i % key.length);
      result += String.fromCharCode(charCode);
    }
    return result;
  } catch {
    return '';
  }
}

/**
 * Loads settings from localStorage
 * @returns {Object} Settings object
 */
export function load() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // If the stored key is plain text (starts with AIza), encrypt it on next save
      if (parsed.apiKey && !parsed.apiKey.startsWith('AIza')) {
        parsed.apiKey = decrypt(parsed.apiKey);
      }
      currentSettings = { ...DEFAULTS, ...parsed };
    }
    updatedAt = JSON.parse(localStorage.getItem(UPDATED_KEY)) ?? {};
  } catch {
    currentSettings = { ...DEFAULTS };
    updatedAt = {};
  }
  stampLegacySettings();
  return { ...currentSettings };
}

/**
 * Einstellungen von vor der Synchronisation haben kein Änderungsdatum. Weicht
 * eine vom Standard ab, wurde sie irgendwann bewusst gesetzt: Sie bekommt den
 * Zeitpunkt 1 – damit schlägt sie den Standard eines neuen Geräts, verliert aber
 * gegen jede echte Änderung.
 */
function stampLegacySettings() {
  for (const [key, value] of Object.entries(currentSettings)) {
    if (!(key in updatedAt) && value !== DEFAULTS[key]) updatedAt[key] = 1;
  }
}

/**
 * Saves settings to localStorage
 * @param {Object} updates - Partial settings to update
 * @param {{ byUser?: boolean }} [options] - byUser: false für Werte, die die
 *   App selbst wählt (z. B. eine Standardstimme). Sie zählen beim Abgleich
 *   nicht als Änderung und überschreiben so keine Wahl von einem anderen Gerät.
 * @returns {Object} Updated settings
 */
export function save(updates, { byUser = true } = {}) {
  currentSettings = { ...currentSettings, ...updates };
  const keys = Object.keys(updates);
  // Automatisch gewählte Werte bekommen den Zeitpunkt 0: Sie verlieren gegen
  // jede echte Wahl und gelten auch nach dem Neuladen nicht als „früher gesetzt“.
  const stamp = byUser ? Date.now() : 0;
  for (const key of keys) updatedAt[key] = stamp;
  persist();
  if (byUser) {
    for (const listener of changeListeners) listener(keys);
  }
  return { ...currentSettings };
}

/**
 * Writes settings and their timestamps to localStorage
 */
function persist() {
  try {
    const serialized = { ...currentSettings };
    if (serialized.apiKey) {
      serialized.apiKey = encrypt(serialized.apiKey);
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(serialized));
    localStorage.setItem(UPDATED_KEY, JSON.stringify(updatedAt));
  } catch {
    // localStorage might be full or unavailable
  }
}

/**
 * Liefert ausgewählte Einstellungen samt Änderungszeitpunkten für den Abgleich
 * @param {string[]} keys
 * @returns {{ values: Object, updatedAt: Object<string, number> }}
 */
export function getSyncedSettings(keys) {
  const values = {};
  const times = {};
  for (const key of keys) {
    if (key in currentSettings) values[key] = currentSettings[key];
    if (key in updatedAt) times[key] = updatedAt[key];
  }
  return { values, updatedAt: times };
}

/**
 * Übernimmt abgeglichene Einstellungen samt ihren Zeitpunkten – aber keine,
 * die hier inzwischen neuer sind (geändert, während der Abgleich lief). Meldet
 * keine Änderung, sonst stieße das Übernehmen den nächsten Abgleich an.
 * @param {{ values: Object, updatedAt: Object<string, number> }} synced
 */
export function applySyncedSettings(synced) {
  for (const [key, value] of Object.entries(synced.values)) {
    const incoming = synced.updatedAt[key] ?? 0;
    if (incoming < (updatedAt[key] ?? 0)) continue;
    currentSettings[key] = value;
    updatedAt[key] = incoming;
  }
  persist();
}

/**
 * Registers a listener for settings changed by the user
 * @param {(keys: string[]) => void} callback
 * @returns {Function} Unsubscribe function
 */
export function onChange(callback) {
  changeListeners.push(callback);
  return () => {
    const idx = changeListeners.indexOf(callback);
    if (idx >= 0) changeListeners.splice(idx, 1);
  };
}

/**
 * Gets a specific setting value
 * @param {string} key
 * @returns {*}
 */
export function get(key) {
  return currentSettings[key];
}

/**
 * Gets all current settings
 * @returns {Object}
 */
export function getAll() {
  return { ...currentSettings };
}

/**
 * Gets the free tier quota for a given tier
 * @param {string} tier
 * @returns {number}
 */
export function getFreeTierQuota(tier) {
  return FREE_TIER_QUOTAS[tier] || FREE_TIER_QUOTAS.Standard;
}

/**
 * Gets the price per million characters for a given tier
 * @param {string} tier
 * @returns {number}
 */
export function getPrice(tier) {
  return PRICING[tier] || PRICING.Standard;
}

/**
 * Calculates cost for characters beyond the free tier
 * @param {number} totalChars - Total characters used this month
 * @param {string} tier - Model tier
 * @returns {number} Cost in USD
 */
export function calculateCost(totalChars, tier) {
  const freeQuota = getFreeTierQuota(tier);
  const billableChars = Math.max(0, totalChars - freeQuota);
  const pricePerMillion = getPrice(tier);
  return (billableChars / 1_000_000) * pricePerMillion;
}

/**
 * Resets settings to defaults
 */
export function reset() {
  currentSettings = { ...DEFAULTS };
  updatedAt = {};
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(UPDATED_KEY);
  } catch {
    // Ignore
  }
}
